/**
 * Section constants for Addictions v2. The list of addictions themselves is
 * fetched from api-addiction-v2-service (`GET /api/v1/addictions/:patientId`)
 * — this file just holds status enum + band colours + a lookup helper.
 */

/**
 * Status bands — the coloured Status dropdown chip (Figma 2091:3070). Four
 * bands, hexes read via Dev Mode MCP; NO leading dot (the caret sits on the
 * right). `key` matches the backend status enum.
 */
export const ADDICTION_STATUS = [
  { key: "regularly", label: "Regularly", bg: "#fdeaea", fg: "#b91c1c", dot: "#b91c1c" },
  { key: "occasionally", label: "Occasionally", bg: "#fdf1e2", fg: "#b45309", dot: "#b45309" },
  { key: "never", label: "Never", bg: "#f4f5f7", fg: "#4a5565", dot: "#4a5565" },
  { key: "quit", label: "Quit", bg: "#e8f7ef", fg: "#1b7f4c", dot: "#1b7f4c" },
];

export const addictionBand = (key) =>
  ADDICTION_STATUS.find((s) => s.key === key) || ADDICTION_STATUS[2];

// Description word cap — matches the 250-word ceiling used across Diagnosis,
// Family history, Hospitalisation and Allergies. Kept in the section's data
// module so both AddictionModal (Add) and UpdateStatusModal share one source.
export const MAX_DESCRIPTION_WORDS = 250;
export const countWords = (s) => {
  const t = (s || "").trim();
  return t ? t.split(/\s+/).length : 0;
};
export const clampWords = (next, max) => {
  if (countWords(next) <= max) return next;
  const parts = next.split(/(\s+)/);
  let words = 0;
  const out = [];
  for (const chunk of parts) {
    if (/^\s+$/.test(chunk)) { out.push(chunk); continue; }
    if (chunk === "") continue;
    if (words >= max) break;
    out.push(chunk);
    words += 1;
  }
  return out.join("").replace(/\s+$/, "");
};

// "~5 years 2 months" — compact human-readable duration from the
// {years, months, days} triple. Used by both the main Addictions table
// AND the trend audit modal so the two render identically. Em-dash when
// the whole triple is zero. Singular/plural switch on each unit.
/**
 * Board 3071:1855 puts the leading tilde on STARTED WHEN only — it marks the
 * approximation ("how long ago"). TOTAL INTERVAL is an exact span and the
 * board writes it bare: "5 years", "1 year", "2 years".
 */
export const formatDuration = (d, approx = true) => {
  if (!d || (!d.years && !d.months && !d.days)) return "—";
  const parts = [];
  if (d.years) parts.push(`${d.years} year${d.years === 1 ? "" : "s"}`);
  if (d.months) parts.push(`${d.months} month${d.months === 1 ? "" : "s"}`);
  if (d.days) parts.push(`${d.days} day${d.days === 1 ? "" : "s"}`);
  return `${approx ? "~" : ""}${parts.join(" ")}`;
};

// Whether a Duration triple is all zeros — used by the table's Total
// interval fallback and by save-time guards.
export const isZeroDuration = (d) =>
  !d || (!d.years && !d.months && !d.days);

// normalizeDuration — carries overflow so "13 months" becomes
// "1 year 1 month" and "45 days" becomes "1 month 15 days". Days-per-
// month is approximated at 30 (calendar months vary, but the input is
// already a rough estimate and this matches how the frontend renders
// long durations elsewhere in the app). Negative components are
// clamped to zero.
export const normalizeDuration = (d) => {
  const y = Math.max(0, Math.floor(Number((d && d.years) || 0)));
  const m = Math.max(0, Math.floor(Number((d && d.months) || 0)));
  const days = Math.max(0, Math.floor(Number((d && d.days) || 0)));
  let totalDays = days;
  let totalMonths = m + Math.floor(totalDays / 30);
  totalDays = totalDays % 30;
  const totalYears = y + Math.floor(totalMonths / 12);
  totalMonths = totalMonths % 12;
  return { years: totalYears, months: totalMonths, days: totalDays };
};
